import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "./Legal.css";

const A = "/remy";
const SUPPORT = "support@helloremy.ai";

function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="remy-legal" data-testid={`legal-${title.toLowerCase().includes("privacy") ? "privacy" : "terms"}`}>
      <header className="rl-nav">
        <div className="rl-nav__inner">
          <Link to="/" className="rl-brand" data-testid="legal-brand">
            <img className="rl-brand__icon" src={`${A}/icon.png`} alt="" aria-hidden />
            <img className="rl-brand__word" src={`${A}/wordmark.png`} alt="Remy" />
          </Link>
          <Link to="/" className="rl-back" data-testid="legal-back-home">
            <ArrowLeft size={16} /> <span>Back to home</span>
          </Link>
        </div>
      </header>

      <main className="rl-wrap rl-main">
        <p className="rl-eyebrow">Legal</p>
        <h1 className="rl-title">{title}</h1>
        <p className="rl-dates">Effective Date: August 2026 · Last Updated: August 2026</p>
        <div className="rl-prose">{children}</div>
      </main>

      <footer className="rl-footer">
        <div className="rl-footer__grid">
          <nav className="rl-footer__links">
            <Link to="/">Home</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/contact">Contact</Link>
          </nav>
          <img className="rl-footer__sign" src={`${A}/life-feels-easier.png`} alt="Life feels easier with Remy." />
        </div>
      </footer>
    </div>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy">
      <p className="rl-intro">
        Remy (“Remy,” “we,” “us,” or “our”) provides a personal digital assistant designed to help
        users manage everyday information and tasks through conversational interfaces, including text
        messaging and our website at <a href="https://helloremy.ai">https://helloremy.ai</a> (the “Service”).
      </p>
      <p>
        This Privacy Policy explains what information we collect, how we use it, when we disclose it,
        the choices available to you, and how we protect your information.
      </p>
      <p>By using Remy, you acknowledge the practices described in this Privacy Policy.</p>

      <h2>1. Information We Collect</h2>
      <h3>Account and Profile Information</h3>
      <p>
        When you create or request access to Remy, we may collect information such as your name,
        mobile phone number, email address, authentication information, timezone, preferences, and
        account settings.
      </p>
      <p>
        If you join our private-access or invitation list, we may collect your first name, last name,
        phone number, consent information, invitation status, and the source through which you
        requested access.
      </p>
      <h3>Messages and Information You Provide to Remy</h3>
      <p>
        We process the messages, instructions, questions, files, information, and other content you
        choose to send to Remy.
      </p>
      <p>
        This may include information relating to your schedule, tasks, relationships, work,
        preferences, reminders, or other aspects of your life that you voluntarily provide.
      </p>
      <p>
        Because Remy is intended to remember context and help across conversations, certain
        information may be retained as conversation history, preferences, task information, or memory
        associated with your account.
      </p>
      <h3>Google Account Information</h3>
      <p>
        If you choose to connect your Google account, Remy may access Google user data that you
        specifically authorize through Google's OAuth permission process.
      </p>
      <p>As of the effective date of this Privacy Policy, Remy's Google integrations may include:</p>
      <ul>
        <li>
          <strong>Gmail:</strong> read-only access used to help you locate, understand, summarize, and
          answer questions about your email.
        </li>
        <li>
          <strong>Google Calendar:</strong> read-only access used to help you understand your schedule,
          search events, and determine availability.
        </li>
      </ul>
      <p>
        Remy requests Google permissions incrementally and seeks to request only permissions needed for
        features you choose to use.
      </p>
      <p>
        If Remy later introduces features such as sending email or creating or modifying calendar
        events, we will request the additional permissions required for those features and update our
        disclosures where appropriate before using those permissions.
      </p>
      <p>
        You can disconnect your Google account from Remy or revoke Remy's Google permissions through
        your Google Account settings.
      </p>
      <h3>How We Use Google User Data</h3>
      <ul>
        <li>Google user data is used only to provide or improve user-facing Remy features that you request or enable.</li>
        <li>We do not sell Google user data.</li>
        <li>
          We do not use Google user data for advertising, retargeting, interest-based advertising,
          credit decisions, or data-broker purposes.
        </li>
        <li>
          We do not use Google user data to train generalized artificial-intelligence or machine-learning
          models unrelated to providing your Remy experience.
        </li>
        <li>
          Human access to Google user data is restricted. Personnel may access such data only where
          permitted by applicable Google policies and law, such as when you expressly authorize access
          to specific information, when access is reasonably necessary for security or abuse
          investigation, or when legally required.
        </li>
      </ul>
      <p className="rl-legal-note">
        Our use and transfer of information received from Google APIs will adhere to the{" "}
        <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements.
      </p>

      <h2>2. SMS and Mobile Messaging Information</h2>
      <p>Remy is designed to communicate with users through SMS or similar messaging channels.</p>
      <p>When you use Remy by text message, we may process:</p>
      <ul>
        <li>your phone number;</li>
        <li>the content of messages you send and receive through Remy;</li>
        <li>message timestamps;</li>
        <li>delivery and provider status;</li>
        <li>carrier or messaging metadata;</li>
        <li>verification events;</li>
        <li>consent and opt-out records; and</li>
        <li>technical identifiers necessary to prevent duplicate messages and maintain reliable delivery.</li>
      </ul>
      <p>
        Your wireless carrier and our messaging providers may also process information as necessary to
        transmit messages.
      </p>
      <h3>SMS Consent</h3>
      <p>
        By providing your mobile number and affirmatively opting in to Remy's messaging service, you
        consent to receive service-related text messages from Remy at the number you provide.
      </p>
      <p>Message frequency varies depending on how you use Remy.</p>
      <p>Message and data rates may apply.</p>
      <p>
        Consent to receive promotional or marketing text messages, if Remy ever offers them, will be
        obtained separately where required and will not be a condition of purchasing the Service.
      </p>
      <h3>STOP and HELP</h3>
      <p>You may text STOP to stop receiving messages from Remy.</p>
      <p>You may text START to re-enable messages where supported.</p>
      <p>You may text HELP for assistance or contact us at <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>.</p>
      <p>Carriers are not liable for delayed or undelivered messages.</p>
      <p>
        Your mobile opt-in information and consent will not be sold, rented, or transferred to third
        parties for their own marketing or promotional purposes.
      </p>

      <h2>3. Reminders, Tasks, Memory, and Preferences</h2>
      <p>
        When you ask Remy to remember information, create a reminder, maintain a task, or store a
        preference, we process the information necessary to provide that feature.
      </p>
      <p>
        Examples may include reminder text, scheduled times, timezone information, task status,
        preferences, and the conversational context necessary to understand your request.
      </p>
      <p>You control what information you choose to provide to Remy.</p>

      <h2>4. Artificial Intelligence Processing</h2>
      <p>
        Remy uses artificial intelligence and automated systems to understand requests, retrieve
        relevant information, generate responses, organize information, and provide features of the
        Service.
      </p>
      <p>
        Information you submit may be transmitted to service providers that supply artificial-intelligence
        infrastructure or related technical services when necessary to provide Remy.
      </p>
      <p>
        We require service providers to process information subject to applicable agreements and
        security and privacy obligations.
      </p>
      <p>
        Remy may use deidentified or aggregated information to evaluate performance, reliability,
        safety, and product quality where permitted by law. We do not use Google user data for
        unrelated generalized model training.
      </p>

      <h2>5. Technical and Usage Information</h2>
      <p>
        We may automatically collect information relating to the operation and security of the Service,
        including browser or device information, IP address, authentication events, log data, error
        information, feature usage, approximate location derived from IP address, timestamps, and
        security or fraud-prevention signals.
      </p>
      <p>We use this information to operate, secure, troubleshoot, and improve Remy.</p>

      <h2>6. How We Use Personal Information</h2>
      <p>
        We use personal information to provide and personalize Remy; authenticate users; deliver SMS
        messages; connect authorized services; answer requests; operate reminders and tasks; maintain
        conversation continuity and user preferences; provide customer support; detect and prevent
        fraud, abuse, security incidents, and duplicate processing; understand and improve product
        performance; communicate with users about the Service; comply with legal obligations; and
        protect the rights, safety, and integrity of Remy, our users, and others.
      </p>
      <p>
        We do not use personal information for materially different purposes without providing notice
        and obtaining consent where legally required.
      </p>

      <h2>7. How We Disclose Information</h2>
      <p>
        We may disclose information to service providers that help us operate Remy, such as cloud
        hosting, databases, AI processing, authentication, email and calendar integration,
        communications providers, security providers, analytics providers, and payment processors.
      </p>
      <p>
        These providers receive information only as reasonably necessary to perform services for Remy
        and are subject to contractual or other obligations governing their handling of information.
      </p>
      <p>
        We may also disclose information where reasonably necessary to comply with law, court orders,
        legal process, or governmental requests; protect the safety, rights, or property of users,
        Remy, or others; investigate fraud, abuse, or security incidents; or support a merger,
        financing, acquisition, reorganization, bankruptcy, or sale of assets subject to applicable law
        and required notices or consent.
      </p>
      <h3>We Do Not Sell Personal Information</h3>
      <p>Remy does not sell personal information.</p>
      <p>
        Remy does not share personal information for cross-context behavioral advertising or targeted
        advertising as those terms may be defined under applicable U.S. privacy laws.
      </p>
      <p>
        If these practices change, we will update this Privacy Policy and provide any legally required
        choices before implementing the change.
      </p>

      <h2>8. Data Retention</h2>
      <p>
        We retain personal information for as long as reasonably necessary to provide the Service,
        maintain your account and requested features, comply with legal obligations, resolve disputes,
        protect security, and enforce agreements.
      </p>
      <p>Retention varies depending on the nature of the information and why it is processed.</p>
      <p>
        For example, OAuth credentials may be retained while an integration remains connected; reminders
        may be retained while needed to provide and document the reminder service; conversation and
        memory information may be retained while associated with an active account or until deleted
        subject to technical, legal, security, and backup requirements.
      </p>
      <p>
        When information is no longer reasonably necessary, we delete, deidentify, or otherwise dispose
        of it in accordance with applicable requirements.
      </p>

      <h2>9. Security</h2>
      <p>
        We use reasonable administrative, technical, and organizational safeguards designed to protect
        personal information against unauthorized access, disclosure, alteration, loss, or misuse.
      </p>
      <p>
        No Internet service or storage system can be guaranteed to be completely secure. You are
        responsible for protecting access to your devices, phone number, account credentials, and
        connected services.
      </p>
      <p>
        If you believe your account has been compromised, contact us promptly at{" "}
        <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>.
      </p>

      <h2>10. Your Privacy Choices and Rights</h2>
      <p>
        Depending on where you live, you may have rights concerning your personal information, including
        the right to request access to information we process about you, correct inaccurate information,
        delete certain information, obtain certain information in portable form, withdraw consent, or
        appeal certain privacy-request decisions.
      </p>
      <p>
        Where applicable, you may also have rights to opt out of certain targeted advertising, sales of
        personal information, or profiling activities.
      </p>
      <p>Remy currently does not sell personal information or use it for targeted advertising.</p>
      <p>To submit a privacy request, email <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>.</p>
      <p>We may take reasonable steps to verify your identity before completing a request.</p>
      <p>
        If we decline a request and applicable law gives you an appeal right, you may appeal by replying
        to our decision or contacting <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> with the subject line
        Privacy Appeal.
      </p>
      <p>We will not discriminate against you for exercising applicable privacy rights.</p>
      <p>
        You may also revoke connected Google access through Remy or your Google Account, and you may
        stop SMS messages by replying STOP.
      </p>

      <h2>11. Children</h2>
      <p>
        Remy is not intended for individuals under 18 years old, and we do not knowingly offer accounts
        to children.
      </p>
      <p>
        If we learn that we have collected personal information from a child contrary to this policy or
        applicable law, we will take appropriate steps to delete it.
      </p>

      <h2>12. International Processing</h2>
      <p>Remy and our service providers may process information in the United States and other jurisdictions.</p>
      <p>
        Where required, we use appropriate safeguards for cross-border transfers of personal
        information.
      </p>

      <h2>13. Third-Party Services</h2>
      <p>
        Remy may connect with or rely upon third-party services such as Google, telecommunications
        providers, cloud services, or other integrations.
      </p>
      <p>
        Your use of those third-party services may also be governed by their own privacy policies and
        terms.
      </p>

      <h2>14. Changes to This Privacy Policy</h2>
      <p>We may update this Privacy Policy as Remy evolves.</p>
      <p>
        If we make material changes, we will provide notice through the Service, our website, SMS,
        email, or another appropriate method where required.
      </p>
      <p>The “Last Updated” date above indicates when this Privacy Policy was most recently revised.</p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of Service">
      <p className="rl-intro">
        These Terms of Service (“Terms”) govern your access to and use of Remy, including helloremy.ai,
        Remy's SMS service, connected integrations, and related products and services (collectively, the
        “Service”).
      </p>
      <p>
        By creating an account, requesting access, connecting an integration, texting Remy, or otherwise
        using the Service, you agree to these Terms.
      </p>
      <p>If you do not agree, do not use the Service.</p>

      <h2>1. What Remy Is</h2>
      <p>
        Remy is a conversational digital assistant designed to help users organize information,
        understand email and calendars, manage reminders and tasks, remember user-provided context, and
        perform other supported everyday-assistance functions.
      </p>
      <p>Features may change, be added, restricted, or removed over time.</p>
      <p>Certain features may initially be offered only to invited users.</p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 18 years old and legally capable of entering into a binding agreement to
        use Remy.
      </p>
      <p>You may use Remy only in compliance with applicable law.</p>

      <h2>3. Accounts</h2>
      <p>
        You are responsible for providing accurate account information and maintaining the security of
        your account, devices, credentials, connected accounts, and phone number.
      </p>
      <p>
        You may not impersonate another person, falsely claim ownership of a phone number or connected
        account, or attempt to access another user's account or data.
      </p>
      <p>Notify us promptly if you suspect unauthorized use.</p>

      <h2>4. Connected Accounts and Permissions</h2>
      <p>Remy may allow you to connect third-party services such as Gmail or Google Calendar.</p>
      <p>
        By connecting a service, you authorize Remy to access and process the information and permissions
        you approve solely to provide supported Remy features.
      </p>
      <p>
        You remain responsible for your relationship with the third-party provider and for ensuring you
        have authority to connect the account.
      </p>
      <p>You may revoke a connection at any time.</p>
      <p>
        At launch, certain integrations may be read-only. Remy will not represent an action as completed
        unless the applicable feature and authorization actually permit that action.
      </p>

      <h2>5. SMS Messaging Terms</h2>
      <p>
        By voluntarily providing your mobile phone number and opting into Remy messaging, you consent to
        receive text messages necessary to provide the Service, including account verification, responses
        to your requests, reminders, service notices, and other communications you have requested or
        authorized.
      </p>
      <p>Message frequency varies.</p>
      <p>Message and data rates may apply.</p>
      <p>You may unsubscribe from SMS messages at any time by replying STOP.</p>
      <p>You may obtain assistance by replying HELP or contacting <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>.</p>
      <p>Where supported, you may reply START to resubscribe.</p>
      <p>Carriers are not liable for delayed or undelivered messages.</p>
      <p>
        Your consent applies only to the phone number and messaging program for which it was provided.
        Consent is not transferable to another recipient.
      </p>
      <p>
        For details on how we collect, use, and protect messaging data, see our{" "}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <h2>6. Reminders and Notifications</h2>
      <p>Remy may send reminders or notifications based on instructions you provide.</p>
      <p>
        You are responsible for confirming that dates, times, timezones, recipients, and reminder
        instructions are correct.
      </p>
      <p>
        Telecommunications outages, device settings, provider delays, network problems, or technical
        failures may delay or prevent delivery.
      </p>
      <p>Do not rely on Remy for emergency alerts or situations where failure or delay could cause serious harm.</p>

      <h2>7. Artificial Intelligence and Accuracy</h2>
      <p>Remy uses artificial intelligence.</p>
      <p>
        AI-generated responses may occasionally be inaccurate, incomplete, outdated, misleading, or
        inappropriate for your circumstances.
      </p>
      <p>You should independently verify important information before relying on it.</p>
      <p>Remy is designed to assist your judgment, not replace it.</p>

      <h2>8. No Professional Advice</h2>
      <p>
        Remy does not provide legal, medical, investment, tax, accounting, emergency, or other regulated
        professional advice.
      </p>
      <p>Information provided through Remy is for general assistance and informational purposes.</p>
      <p>Consult an appropriately qualified professional before making decisions in high-stakes areas.</p>
      <p>
        Do not use Remy as an emergency service. If you are experiencing an emergency, contact the
        appropriate emergency provider directly.
      </p>

      <h2>9. Actions and Approvals</h2>
      <p>
        Certain current or future Remy features may prepare, recommend, schedule, send, modify, or
        execute actions on your behalf.
      </p>
      <p>
        Where Remy requests approval before an external action, you are responsible for reviewing the
        recipient, content, timing, amount, or other relevant details before approving.
      </p>
      <p>Approval applies only to the specific action presented.</p>
      <p>Remy may refuse or require additional confirmation for sensitive, high-risk, prohibited, or unsupported actions.</p>
      <p>
        No Remy response stating that an action occurred should be treated as proof of completion unless
        the Service confirms successful execution.
      </p>

      <h2>10. Your Content</h2>
      <p>You retain ownership of content and information you submit to Remy.</p>
      <p>
        You grant Remy a limited, non-exclusive right to host, process, transmit, reproduce, and use that
        content as reasonably necessary to provide, secure, maintain, and improve the Service in
        accordance with our Privacy Policy.
      </p>
      <p>You represent that you have the right to provide any information or content you submit.</p>

      <h2>11. Acceptable Use</h2>
      <p>
        You may not use Remy to violate law; infringe another person's rights; access accounts or
        information without authorization; send spam, harassment, threats, malware, or fraudulent
        communications; impersonate another person; exploit or abuse minors; interfere with the security
        or operation of the Service; bypass usage, safety, authentication, or authorization controls;
        reverse engineer the Service except where law expressly permits; attempt to extract secrets,
        credentials, system prompts, or confidential system information; use Remy to develop malware or
        conduct unauthorized surveillance; or use the Service for any unlawful or harmful purpose.
      </p>
      <p>
        We may investigate, restrict, suspend, or terminate use that violates these Terms or presents
        security, legal, or safety risks.
      </p>

      <h2>12. Private Access and Beta Features</h2>
      <p>Some features may be labeled preview, private access, beta, experimental, or similar.</p>
      <p>
        Such features may contain errors, change substantially, experience interruptions, or be
        discontinued without notice.
      </p>
      <p>Your feedback is welcome, but you should not rely on preview features for critical activities.</p>

      <h2>13. Fees and Subscriptions</h2>
      <p>Certain Remy services may be offered for a fee.</p>
      <p>
        If paid plans are introduced, pricing, billing frequency, included usage, renewal terms,
        cancellation procedures, and any applicable trial terms will be disclosed before purchase.
      </p>
      <p>
        Unless required by law or otherwise stated at purchase, fees already incurred are non-refundable.
      </p>
      <p>We may change pricing prospectively with required notice.</p>

      <h2>14. Third-Party Services</h2>
      <p>Remy depends on third-party providers and integrations.</p>
      <p>
        We are not responsible for third-party products, services, outages, terms, policies, changes, or
        actions outside our reasonable control.
      </p>
      <p>Third-party services may discontinue or alter access at any time, which may affect Remy features.</p>

      <h2>15. Intellectual Property</h2>
      <p>
        Remy, including its software, branding, logos, visual design, interfaces, documentation, and
        other materials provided by us, is owned by HelloRemy or its licensors and is
        protected by intellectual-property laws.
      </p>
      <p>Except for the limited right to use the Service under these Terms, no rights are transferred to you.</p>
      <p>“Remy,” the Remy character/icon, and associated brand assets may be trademarks of HelloRemy.</p>

      <h2>16. Feedback</h2>
      <p>
        If you voluntarily provide suggestions, ideas, or feedback concerning Remy, you grant us
        permission to use that feedback without restriction or compensation, provided we do not publicly
        identify you as the source without permission.
      </p>

      <h2>17. Suspension and Termination</h2>
      <p>You may stop using Remy at any time.</p>
      <p>
        We may suspend or terminate your access if you violate these Terms, create security or legal
        risk, misuse the Service, fail to pay amounts due, or where suspension is reasonably necessary to
        protect users or the Service.
      </p>
      <p>Where practical, we will provide notice.</p>
      <p>Sections that by their nature should survive termination will survive.</p>

      <h2>18. Disclaimer of Warranties</h2>
      <p className="rl-caps">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, REMY IS PROVIDED “AS IS” AND “AS AVAILABLE.”
      </p>
      <p className="rl-caps">
        WE DISCLAIM WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND
        ANY IMPLIED WARRANTIES TO THE EXTENT PERMITTED BY LAW.
      </p>
      <p className="rl-caps">
        WE DO NOT WARRANT THAT THE SERVICE WILL ALWAYS BE AVAILABLE, ERROR-FREE, SECURE, ACCURATE, OR
        SUITABLE FOR EVERY PURPOSE.
      </p>
      <p>Nothing in these Terms excludes warranties or rights that cannot lawfully be excluded.</p>

      <h2>19. Limitation of Liability</h2>
      <p className="rl-caps">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, HelloRemy AND ITS AFFILIATES, OFFICERS,
        EMPLOYEES, CONTRACTORS, AND SERVICE PROVIDERS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL,
        SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, LOST REVENUE, LOST
        DATA, OR BUSINESS INTERRUPTION ARISING FROM OR RELATING TO THE SERVICE.
      </p>
      <p className="rl-caps">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY ARISING OUT OF OR RELATING TO THE
        SERVICE WILL NOT EXCEED THE GREATER OF:
      </p>
      <ul>
        <li>THE AMOUNT YOU PAID TO REMY DURING THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM; OR</li>
        <li>US $100.</li>
      </ul>
      <p>
        Some jurisdictions do not allow certain liability limitations, so portions of this section may
        not apply to you.
      </p>

      <h2>20. Indemnification</h2>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless HelloRemy
        from claims, losses, liabilities, costs, and expenses arising from your unlawful use of the
        Service, your violation of these Terms, or content you submit that violates another person's
        rights.
      </p>
      <p>This provision does not apply where prohibited by consumer-protection law.</p>

      <h2>21. Governing Law</h2>
      <p>
        These Terms are governed by the laws of the State of Texas, without regard to conflict-of-law
        rules.
      </p>
      <p>
        Unless applicable law requires otherwise, disputes that are not resolved informally will be
        brought in the state or federal courts located in Harris County, Texas, and each party consents to
        their jurisdiction.
      </p>

      <h2>22. Changes to These Terms</h2>
      <p>We may update these Terms as the Service changes.</p>
      <p>If a change materially affects your rights, we will provide reasonable notice where required.</p>
      <p>
        Continued use after updated Terms become effective constitutes acceptance to the extent
        permitted by law.
      </p>
    </LegalShell>
  );
}
